import React, { useCallback } from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  FiberManualRecord as FiberManualRecordIcon,
  GppGood,
  Lock,
} from "@mui/icons-material";
import { colors } from "../../../ui/colors";
import { ISecretDTO } from "../../interfaces/ISecretDTO";
import { useDetailsCardVault } from "../../hooks/useDetailsCardVault";
import { useModals } from "../../../../context/ModalContext";
import formatMMSS from "../helpper/formatMMSS";
import SecretDetail from "../../types/SecretDetail";
import VerificationModal from "./VerificationModal";
import { BaseModalComponent } from "../../../common/BaseModalComponent";

type Props = {
  secretId: string;
  secret: SecretDetail;
  unlocked: boolean;
  revealSeconds?: number;
  onDelete?: (id: string) => Promise<void> | void;
  onUpdate?: (id: string, dto: ISecretDTO) => Promise<void> | void;
  vaultRemainingSeconds?: number;
  isVerified?: boolean;
  modalKey: string;
  closeOnSave?: boolean;
  isPreviewMode?: boolean;
};

export default function DetailsCardVault({
  secretId,
  secret,
  unlocked,
  revealSeconds = 8,
  onDelete,
  onUpdate,
  modalKey,
  closeOnSave = false,
  vaultRemainingSeconds,
  isVerified = false,
  isPreviewMode = false,
}: Props) {
  const { modals, closeModal, showDialog } = useModals();
  const verifyModalKey = `${modalKey}-verify`;

  // --- LÓGICA DE CIERRE Y ELIMINACIÓN ---
  const closeThisModal = useCallback(() => {
    const candidates = (modals || []).filter((m: any) => m.key === modalKey && !m.minimized);
    if (!candidates.length) return;
    const modalToClose = candidates[candidates.length - 1];
    closeModal(modalToClose.id);
  }, [modals, closeModal, modalKey]);

  const handleDeleteClick = useCallback(() => {
    if (!onDelete) return;
    showDialog({
      type: "confirm",
      title: "Delete secret?",
      message: "This action cannot be undone. The secret will be permanently deleted.",
      confirmLabel: "Yes, delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        closeThisModal();
        await onDelete(secretId);
      },
    });
  }, [onDelete, secretId, showDialog, closeThisModal]);

  // --- HOOK DE NEGOCIO ---
  const {
    isEditing,
    saving,
    draft,
    handleDraftChange,
    startEdit,
    cancelEdit,
    saveEdit,
    canSave,
    handleReveal,
    revealed,
    revealedPassword,
    masked,
    remainingSeconds: localRemainingSeconds,
    statusColor,
  } = useDetailsCardVault({
    secretId,
    secret,
    unlocked,
    revealSeconds,
    onUpdate,
    onAfterSave: closeOnSave ? closeThisModal : undefined,
    isVerified,
  });

  const website = (isEditing ? draft.websiteUrl : secret.websiteUrl)?.trim();

  return (
    <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 3, bgcolor: "background.paper" }}>

      {/* HEADER SECTION */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box sx={{ bgcolor: colors.orange.base, width: 32, height: 32, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
            <GppGood />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {isEditing ? (
              <TextField
                size="small"
                value={draft.title}
                onChange={(e) => handleDraftChange("title", e.target.value)}
                sx={{ minWidth: 280 }}
                inputProps={{ style: { fontWeight: 800 } }}
              />
            ) : (
              <Typography variant="h6" fontWeight={800} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {secret.title}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {typeof vaultRemainingSeconds === "number" && (
            <Typography variant="caption" sx={{ color: colors.orange.base, fontWeight: 800 }}>
              Auto-lock in {formatMMSS(vaultRemainingSeconds)}
            </Typography>
          )}

          <Avatar src={secret.imageUrl ?? undefined} sx={{ width: 36, height: 36 }} />
          {secret.highSecurity && <Lock sx={{ color: colors.orange.base, fontSize: 18 }} />}

          <Tooltip title="Delete">
            <IconButton size="small" color="inherit" onClick={handleDeleteClick} disabled={!unlocked || isPreviewMode}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>

          {!isEditing ? (
            <IconButton
              size="small"
              onClick={startEdit}
              disabled={isPreviewMode}
              sx={{ bgcolor: isPreviewMode ? "grey.300" : colors.orange.base, color: "white", "&:hover": { background: isPreviewMode ? "grey.300" : "#e26900" }, borderRadius: 1, px: 1.5 }}
            >
              <EditIcon fontSize="small" />
              <Typography component="span" sx={{ ml: 0.5, fontWeight: 700, fontSize: 14 }}>Edit</Typography>
            </IconButton>
          ) : (
            <>
              <IconButton size="small" onClick={cancelEdit}><CloseIcon /></IconButton>
              <IconButton
                size="small"
                onClick={saveEdit}
                disabled={!canSave || isPreviewMode}
                sx={{
                  bgcolor: (canSave && !isPreviewMode) ? colors.orange.base : "grey.300",
                  color: "white",
                  "&:hover": { background: (canSave && !isPreviewMode) ? "#e26900" : "grey.300" },
                  borderRadius: 1, px: 1.5
                }}
              >
                <SaveIcon fontSize="small" />
                <Typography component="span" sx={{ ml: 0.5, fontWeight: 700, fontSize: 14 }}>
                  {saving ? "Saving..." : "Save"}
                </Typography>
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* LOGIN DETAILS */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 1 }}>LOGIN DETAILS</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", p: 2, bgcolor: "background.paper" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 1.2, columnGap: 2, alignItems: "center" }}>

            <Typography variant="body2">Username</Typography>
            {isEditing ? (
              <TextField size="small" value={draft.usernameEmail} onChange={(e) => handleDraftChange("usernameEmail", e.target.value)} fullWidth />
            ) : (
              <Box sx={{ background: colors.gray[100], border: 1, borderColor: "grey.400", borderRadius: 1, px: 1.5, py: 0.5, fontWeight: 600, boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)" }}>
                {secret.usernameEmail}
              </Box>
            )}

            <Divider sx={{ gridColumn: "1 / -1" }} />

            <Typography variant="body2">Password</Typography>
            {isEditing ? (
              <TextField
                size="small"
                value={draft.password?.startsWith("U2Fsd") ? "" : draft.password}
                onChange={(e) => handleDraftChange("password", e.target.value)}
                placeholder="Hire your new password..."
                fullWidth />
            ) : (
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Box sx={{
                  flex: 1, background: colors.gray[100], border: 1, borderColor: "grey.400", borderRadius: 1, px: 1.5, py: 0.5,
                  fontWeight: 600, boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",

                }}>
                  {revealed ? revealedPassword : masked}
                </Box>

                {(revealed || !secret.highSecurity) ? (
                  <Typography variant="body2" color="primary" fontWeight={700} sx={{ cursor: "pointer" }} onClick={() => handleReveal()}>
                    {revealed ? `HIDE (${localRemainingSeconds}s)` : `REVEAL (${revealSeconds}s)`}
                  </Typography>
                ) : (
                  <BaseModalComponent
                    modalKey={verifyModalKey}
                    modalLabel="Seguridad Requerida"
                    defaultSizes={{ width: 500, height: "auto" }}
                    elementTrigger={
                      <Typography variant="body2" color="primary" fontWeight={700}
                        sx={{ cursor: "pointer" }}>REVEAL ({revealSeconds}s)</Typography>
                    }
                  >
                    <VerificationModal
                      modalKey={verifyModalKey}
                      id={secretId}
                      itemTitle={secret.title}
                      emailMasked={secret.usernameEmail}
                      onVerified={(data, code) => {
                        handleReveal(code, data);
                        const currentModal = modals?.find(m => m.key === verifyModalKey);
                        if (currentModal) closeModal(currentModal.id);
                      }}
                    />
                  </BaseModalComponent>
                )}
              </Box>
            )}

            <Divider sx={{ gridColumn: "1 / -1" }} />

            <Typography variant="body2">Website</Typography>
            {isEditing ? (
              <TextField size="small" value={draft.websiteUrl ?? ""} onChange={(e) => handleDraftChange("websiteUrl", e.target.value)} placeholder="https://..." fullWidth />
            ) : website ? (
              <Link href={website} target="_blank" rel="noopener" underline="hover" sx={{
                background: colors.gray[100], border: 1, borderColor: "grey.400", borderRadius: 1, px: 1.5, py: 0.5,
                fontWeight: 600, color: colors.orange.base, boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)"
              }}>
                {website}
              </Link>
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* NOTES */}
      <Box>
        <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 1, mb: 1 }}>NOTES</Typography>
        {isEditing ? (
          <TextField multiline rows={4} value={draft.notes ?? ""} onChange={(e) => handleDraftChange("notes", e.target.value)} fullWidth />
        ) : (
          <Box sx={{ bgcolor: "var(--fx-surface-sunken, #F8FAFC)", borderRadius: 2, p: 2, fontSize: 14, whiteSpace: "pre-line", minHeight: 80 }}>
            {secret.notes?.trim() ? secret.notes : "—"}
          </Box>
        )}
      </Box>

      <Divider />

      {/* ROLES & SECURITY HEALTH */}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 1, mb: 1 }}>ROLES & ACCESS</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {(secret.roles ?? []).length ? (
              secret.roles!.map((role) => <Chip key={role} label={role} size="small" />)
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 200, borderLeft: { xs: "none", sm: "1px solid var(--fx-surface-border, #E2E8F0)" }, pl: { sm: 2 } }}>
          <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 1, mb: 1 }}>SECURITY HEALTH</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FiberManualRecordIcon sx={{ fontSize: 12, color: statusColor(secret.securityHealth?.status) }} />
            <Typography variant="body2" fontWeight={600}>
              {secret.securityHealth?.status ? `${secret.securityHealth.status.charAt(0).toUpperCase()}${secret.securityHealth.status.slice(1)} Password` : "—"}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}